import { useState } from "react";
import { FaSearch, FaTimes } from "react-icons/fa";
import styled from "styled-components";

const SearchInput = ({
  onSearch,
  placeholder = "Search...",
  onReset,
}: {
  onSearch: (query: string) => void;
  placeholder?: string;
  onReset: () => void;
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  const onResetSearch = () => {
    setSearchTerm("");
    onReset();
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      onSearch(searchTerm);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length === 0) {
      onReset();
    }
    setSearchTerm(e.target.value);
  };

  return (
    <SearchWrapper>
      <SearchContainer>
        <StyledInput
          type="text"
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleChange}
          onKeyDown={handleKeyPress}
          hasResetIcon={!!searchTerm}
        />
        {searchTerm && (
          <ResetIcon onClick={onResetSearch}>
            <FaTimes />
          </ResetIcon>
        )}
        <SearchIcon onClick={() => onSearch(searchTerm)}>
          <FaSearch />
        </SearchIcon>
      </SearchContainer>
    </SearchWrapper>
  );
};

export default SearchInput;

const SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  min-width: 280px;
`;

const SearchContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 320px;
`;

const StyledInput = styled.input<{ hasResetIcon: boolean }>`
  width: 100%;
  height: 40px;
  padding: 0 45px 0 16px;
  padding-right: ${({ hasResetIcon }) => (hasResetIcon ? "80px" : "45px")};
  border: 1.5px solid ${({ theme }) => theme.colors?.border || "#e2e8f0"};
  border-radius: 12px;
  font-size: 14px;
  font-weight: 400;
  background-color: ${({ theme }) => theme.widget?.background || "#ffffff"};
  color: ${({ theme }) => theme.text || "#1a202c"};
  transition: all 0.2s ease-in-out;
  outline: none;

  &::placeholder {
    color: ${({ theme }) => theme.colors?.textSecondary || "#a0aec0"};
    font-weight: 400;
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors?.primary || "#3b82f6"};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors?.primary || "#3b82f6"}20;
    background-color: ${({ theme }) => theme.widget?.background || "#ffffff"};
  }

  &:hover:not(:focus) {
    border-color: ${({ theme }) => theme.colors?.borderHover || "#cbd5e0"};
  }
`;

const SearchIcon = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  color: ${({ theme }) => theme.colors?.textSecondary || "#a0aec0"};
  transition: all 0.2s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${({ theme }) => theme.colors?.primary || "#3b82f6"};
    background-color: ${({ theme }) => theme.colors?.primary || "#3b82f6"}10;
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ResetIcon = styled.button`
  position: absolute;
  right: 48px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  color: ${({ theme }) => theme.colors?.textSecondary || "#a0aec0"};
  transition: all 0.2s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${({ theme }) => theme.colors?.danger || "#ef4444"};
    background-color: ${({ theme }) => theme.colors?.danger || "#ef4444"}10;
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;
